import { observable, action, computed, makeObservable, runInAction } from "mobx";
import { API_URL } from "General/APIEndpoints";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API } from "General/API";

export interface ProjectUsersDirectoryParams extends API_UserPagination, API_UserFilter {}

const isUnassignedRole = (role?: API_PROJECTUSER_USERPROJECTROLE) => !role || role === "NONE";

/**
 * Users coming from the paginated `api/users` directory carry no project membership data,
 * so unassigned defaults are applied until the user is edited.
 */
const directoryUserToProjectUser = (user: API_User): API_ProjectUser => ({
  id: user.id,
  userId: user.userId,
  username: [user.firstName, user.surname].filter(Boolean).join(" "),
  company: user.companyName,
  email: user.email,
  active: user.active,
  projectMembershipActive: false,
  userProjectRole: null,
});

export class ProjectUsersStore extends CoreStore {
  /* Pristine project members as received from the server, never touched by edits */
  serverData: API_ProjectUser[] = [];
  /* Rows edited by the user, sent as-is on save */
  updatedData: API_ProjectUser[] = [];
  /* Current page of the `api/users` directory, used when showing users beyond the project members */
  directoryItems: API_User[] = [];
  directoryTotal = 0;

  constructor() {
    super();

    makeObservable(this, {
      serverData: observable,
      updatedData: observable,
      directoryItems: observable,
      directoryTotal: observable,
      data: computed,
      assignedUsers: computed,
      directoryRows: computed,
      isDirty: computed,
      pendingById: computed,
      baselineById: computed,
      saveUsers: action.bound,
      clear: action.bound,
      fetch: action.bound,
      fetchDirectory: action.bound,
      updateUser: action.bound,
      setProjectMembership: action.bound,
      setUserProjectRole: action.bound,
      setValidUntil: action.bound,
      resetChanges: action.bound,
    });
  }

  url(id?: string | number) {
    return API_URL.ADMIN_PROJECTS.getProjectUsers(id);
  }

  get pendingById(): Map<number, API_ProjectUser> {
    return new Map(this.updatedData.filter((user) => user.id != null).map((user) => [user.id as number, user]));
  }

  get baselineById(): Map<number, API_ProjectUser> {
    return new Map(this.serverData.filter((user) => user.id != null).map((user) => [user.id as number, user]));
  }

  /* All project members with pending edits applied */
  get data(): API_ProjectUser[] {
    const pending = this.pendingById;
    return this.serverData.map((user) => (user.id != null && pending.get(user.id)) || user);
  }

  get assignedUsers(): API_ProjectUser[] {
    return this.data.filter((user) => !isUnassignedRole(user.userProjectRole));
  }

  /* Current directory page, resolved to editable project member rows */
  get directoryRows(): API_ProjectUser[] {
    const pending = this.pendingById;
    const baseline = this.baselineById;
    return this.directoryItems.map((item) => {
      if (item.id == null) {
        return directoryUserToProjectUser(item);
      }
      return pending.get(item.id) || baseline.get(item.id) || directoryUserToProjectUser(item);
    });
  }

  get isDirty(): boolean {
    return this.updatedData.length > 0;
  }

  clear() {
    this.serverData = [];
    this.updatedData = [];
    this.directoryItems = [];
    this.directoryTotal = 0;
  }

  @handleStoreState()
  async fetch(id?: string | number) {
    const response: API_ProjectUserResponse = await API.getData(this.url(id));
    runInAction(() => {
      this.serverData = (response?.users ?? []).map((user) => ({ ...user }));
      this.updatedData = [];
    });
  }

  @handleStoreState()
  async fetchDirectory(params: ProjectUsersDirectoryParams) {
    try {
      const response: API_MultiUserResponse = await API.getData(API_URL.USERS.getUsers(), params);
      runInAction(() => {
        this.directoryItems = response?.items ?? [];
        this.directoryTotal = response?.total ?? 0;
      });
    } catch (error) {
      throw error;
    }
  }

  async saveUsers(id: string | number) {
    try {
      if (this.updatedData.length === 0) {
        return;
      }
      const savedUsers = this.updatedData.map((user) => ({ ...user }));
      await API.editData(API_URL.ADMIN_PROJECTS.updateProjectUsers(id), { users: savedUsers });
      runInAction(() => {
        this.serverData = this.mergeIntoServerData(savedUsers);
        this.updatedData = [];
      });
    } catch (error) {
      throw error;
    }
  }

  updateUser(id: number, patch: Partial<API_ProjectUser>) {
    const current = this.resolveRow(id);
    if (!current?.active) {
      return;
    }
    const nextUser = { ...current, ...patch };
    this.updatedData = [...this.updatedData.filter((user) => user.id !== id), nextUser];
  }

  setProjectMembership(id: number, active: boolean) {
    const user = this.resolveRow(id);
    if (!user?.active) {
      return;
    }

    if (active) {
      const role = isUnassignedRole(user.userProjectRole) ? "USER" : user.userProjectRole;
      this.updateUser(id, { projectMembershipActive: true, userProjectRole: role });
      return;
    }

    const baseline = this.baselineById.get(id);
    if (isUnassignedRole(baseline?.userProjectRole)) {
      this.updateUser(id, { projectMembershipActive: false, userProjectRole: "NONE" });
      return;
    }

    this.updateUser(id, { projectMembershipActive: false });
  }

  setUserProjectRole(id: number, role: API_PROJECTUSER_USERPROJECTROLE) {
    this.updateUser(id, { userProjectRole: role });
  }

  setValidUntil(id: number, timestamp: number) {
    this.updateUser(id, { validUntil: timestamp });
  }

  resetChanges() {
    this.updatedData = [];
  }

  /* Pending edit first, then the project members response, then the current directory page */
  private resolveRow(id: number): API_ProjectUser | undefined {
    const pending = this.pendingById.get(id);
    if (pending) {
      return pending;
    }
    const baseline = this.baselineById.get(id);
    if (baseline) {
      return baseline;
    }
    const directoryItem = this.directoryItems.find((item) => item.id === id);
    return directoryItem ? directoryUserToProjectUser(directoryItem) : undefined;
  }

  /* Saved rows become the new baseline; users assigned from the directory are appended */
  private mergeIntoServerData(savedUsers: API_ProjectUser[]): API_ProjectUser[] {
    const savedById = new Map(savedUsers.filter((user) => user.id != null).map((user) => [user.id as number, user]));
    const merged = this.serverData.map((user) => {
      const saved = user.id != null ? savedById.get(user.id) : undefined;
      if (saved) {
        savedById.delete(user.id as number);
        return saved;
      }
      return { ...user };
    });
    return [...merged, ...savedById.values()];
  }
}
